import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { setPortalToken, setPortalUser, parsePortalToken } from '../../services/portalApi';
import { CK, FONT_BODY } from '../../pages/portal/portalStyles';

// Read a cookie by name
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Clear the Portal SSO token cookie after reading
function clearPortalSSOCookie() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  document.cookie = `portal_sso_token=; Path=/; Max-Age=0`;
  for (let i = 0; i < parts.length; i++) {
    const domain = '.' + parts.slice(i).join('.');
    document.cookie = `portal_sso_token=; Domain=${domain}; Path=/; Max-Age=0`;
  }
}

const PortalSSOCallback = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const handleToken = async () => {
      // Read token from cookie (primary) or hash fragment (fallback)
      let token = getCookie('portal_sso_token') || getCookie('cloudtrail_sso_token');

      if (!token) {
        const hash = window.location.hash;
        if (hash.startsWith('#token=')) {
          token = decodeURIComponent(hash.substring(7));
        }
      }

      // Clean up immediately
      clearPortalSSOCookie();
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      if (!token) {
        setError('No authentication token found. Please try logging in again.');
        setLoading(false);
        return;
      }

      try {
        const claims = parsePortalToken(token);
        if (!claims) {
          throw new Error('Invalid token format');
        }

        setPortalToken(token);
        setPortalUser({
          email: claims.email,
          customerId: claims.customerId,
          realm: claims.realm,
          services: claims.services || [],
        });

        setLoading(false);
        setSuccess(true);
        toast.success('Authentication successful!');

        setTimeout(() => {
          if (onLogin) {
            onLogin({
              token,
              email: claims.email,
              customerId: claims.customerId,
              realm: claims.realm,
              services: claims.services || [],
            });
          }
        }, 1000);
      } catch (err) {
        console.error('Error processing portal token:', err);
        setError('Failed to process authentication. Please try logging in again.');
        setLoading(false);
        toast.error('Authentication failed');
      }
    };

    handleToken();
  }, [onLogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: CK.pageBg,
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: CK.surface,
              border: `1px solid ${CK.border}`,
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img
                src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
                alt="CloudKeeper"
                style={{
                  height: '40px',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  marginBottom: '8px'
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  color: CK.muted,
                  fontFamily: FONT_BODY,
                  fontWeight: 500,
                }}
              >
                Organization Services
              </Typography>
            </Box>

            {/* Loading State */}
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(70, 152, 211, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{ color: CK.blue }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    color: CK.navy,
                    mb: 1,
                  }}
                >
                  Completing Authentication
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: CK.body,
                    fontFamily: FONT_BODY,
                    mb: 2,
                  }}
                >
                  Please wait while we verify your credentials...
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: CK.blue,
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            )}

            {/* Success State */}
            {success && !loading && !error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(39, 174, 96, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 32, color: CK.green }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    color: CK.green,
                    mb: 1,
                  }}
                >
                  Authentication Successful
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: CK.body,
                    fontFamily: FONT_BODY,
                  }}
                >
                  Redirecting to Organization Services...
                </Typography>
              </motion.div>
            )}

            {/* Error State */}
            {error && !loading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 32, color: CK.red }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    color: CK.red,
                    mb: 1.5,
                  }}
                >
                  Authentication Failed
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    border: '1px solid rgba(231, 76, 60, 0.2)',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: CK.body,
                      fontFamily: FONT_BODY,
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                    }}
                  >
                    {error}
                  </Typography>
                </Paper>

                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => window.location.href = '/'}
                  fullWidth
                  sx={{
                    backgroundColor: CK.blue,
                    borderRadius: '6px',
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1.5,
                    boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      backgroundColor: CK.blueDark,
                      boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                    },
                  }}
                >
                  Return to Login
                </Button>
              </motion.div>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${CK.border}` }}>
              <Typography
                variant="caption"
                sx={{
                  color: CK.disabled,
                  fontFamily: FONT_BODY,
                }}
              >
                CloudKeeper Organization Services
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PortalSSOCallback;
